import React, { useEffect, useMemo, useState } from 'react';
import { getTrustProfile, getPolicyAuditTrail, getPhaseGates } from '../../services/api';

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);
const list = (b) => (Array.isArray(b) ? b : (Array.isArray(b?.content) ? b.content : []));
const num = (n) => (n == null ? '—' : Number(n).toLocaleString());

/**
 * One agent, and everything it can prove.
 *
 * <h3>Four tabs, because they answer four different questions</h3>
 *
 * Summary is how it has behaved. Audit trail is what it did. Five-layer verdict
 * is how a call was judged. Phase gates is what it may become. Collapsing any
 * two loses the distinction that makes the record worth keeping.
 *
 * <h3>A score is earned, never assigned</h3>
 *
 * The run-time score comes from governed runs and nowhere else. An agent that
 * has not run says so — not a zero, which nobody established, and not the
 * design-time number wearing the run-time label.
 */
export default function AgentDetail({ agentId, name, archetype, lastAction, onBack }) {
  const [tab, setTab] = useState('summary');
  const [trust, setTrust] = useState(null);
  const [audit, setAudit] = useState([]);
  const [gates, setGates] = useState([]);

  useEffect(() => {
    if (!agentId) return undefined;
    let alive = true;
    (async () => {
      const [t, a, g] = await Promise.all([
        getTrustProfile(agentId).then(unwrap).catch(() => null),
        getPolicyAuditTrail({ agentId, hours: 720 }).then(unwrap).catch(() => null),
        getPhaseGates(agentId).then(unwrap).catch(() => null),
      ]);
      if (!alive) return;
      setTrust(t); setAudit(list(a)); setGates(list(g));
    })();
    return () => { alive = false; };
  }, [agentId]);

  const ran = (trust?.evaluations || 0) > 0 && trust?.currentScore != null;

  const tabs = [
    ['summary', 'Summary'], ['audit', 'Audit trail'],
    ['verdict', 'Five-layer verdict'], ['gates', 'Phase gates'],
  ];

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                    marginBottom: 'var(--p7-space-4)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, font: '700 22px/28px var(--p7-font-sans)',
                       letterSpacing: '-.02em' }}>{name}</h1>
          <p style={{ margin: '1px 0 0', font: 'var(--p7-text-caption)',
                      fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
            {archetype}{lastAction ? ` · last action ${lastAction}` : ''}
          </p>
        </div>
        {onBack ? (
          <button type="button" className="p7-btn p7-btn--sm" onClick={onBack}>Back</button>
        ) : null}
      </div>

      <div role="tablist" style={{ display: 'flex', gap: '2px',
                                   borderBottom: '1px solid var(--p7-line)',
                                   marginBottom: 'var(--p7-space-5)' }}>
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" type="button" aria-selected={tab === k}
                  onClick={() => setTab(k)}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer',
                           padding: '10px 14px', marginBottom: '-1px',
                           font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                           fontWeight: tab === k ? 600 : 500,
                           color: tab === k ? 'var(--p7-accent)' : 'var(--p7-ink-subtle)',
                           borderBottom: `2px solid ${tab === k ? 'var(--p7-accent)' : 'transparent'}` }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'summary' ? <Summary trust={trust} ran={ran} /> : null}
      {tab === 'audit' ? <Audit rows={audit} /> : null}
      {tab === 'verdict' ? <Verdict /> : null}
      {tab === 'gates' ? <Gates gates={gates} trust={trust} ran={ran} /> : null}
    </div>
  );
}

function Summary({ trust, ran }) {
  if (!ran) {
    return (
      <div className="p7-note p7-note--hold">
        This agent has not run under protection yet. A run-time score is earned, not assigned —
        it appears once its first protected actions have been evaluated.
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, trust.currentScore));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                    marginBottom: 'var(--p7-space-3)' }}>
        <h2 className="p7-eyebrow" style={{ margin: 0 }}>Protected</h2>
        <span className="p7-status" style={{ marginLeft: 'auto' }}>
          Last {trust.windowDays || 90} days
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--p7-space-6)', flexWrap: 'wrap',
                    marginBottom: 'var(--p7-space-5)' }}>
        <Stat label="Evaluations" value={num(trust.evaluations)} />
        <Stat label="Allowed" value={num((trust.evaluations || 0) - (trust.blocked || 0))} />
        <Stat label="Blocked" value={num(trust.blocked)} />
        <Stat label="Warned" value={num(trust.warned)} />
      </div>

      <div className="p7-card" style={{ padding: 'var(--p7-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                      marginBottom: 'var(--p7-space-4)' }}>
          <h3 className="p7-eyebrow" style={{ margin: 0 }}>Trust score</h3>
          <span className="p7-status" style={{ marginLeft: 'auto' }}>
            Earned by this agent’s own runs
          </span>
        </div>

        <div style={{ display: 'flex', gap: 'var(--p7-space-5)', flexWrap: 'wrap' }}>
          <div
            aria-hidden="true"
            style={{ width: '106px', height: '106px', borderRadius: '50%', flex: 'none',
                     display: 'grid', placeItems: 'center',
                     background: `conic-gradient(var(--p7-hold) 0 ${pct}%, var(--p7-line) 0)` }}
          >
            <span style={{ width: '82px', height: '82px', borderRadius: '50%',
                           background: 'var(--p7-surface)', display: 'grid',
                           placeItems: 'center', textAlign: 'center' }}>
              <span style={{ font: '700 27px/29px var(--p7-font-sans)',
                             color: 'var(--p7-hold-ink)' }}>{trust.currentScore}</span>
              <span className="p7-eyebrow">{trust.band}</span>
            </span>
          </div>

          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <span className="p7-status p7-status--running">Run time</span>
              <span className="p7-status">From this agent’s protected runs</span>
              <span className="p7-status">Confidence {Number(trust.confidence ?? 0).toFixed(2)}</span>
            </div>
            <p style={{ margin: 'var(--p7-space-3) 0 0', font: 'var(--p7-text-body)',
                        fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-muted)',
                        lineHeight: 1.6 }}>
              How this agent has <i>behaved</i> — whether its protected actions stayed inside the
              policy that binds it, across {num(trust.evaluations)} evaluations over the last{' '}
              {trust.windowDays || 90} days.
            </p>

            <div data-testid="evidence" style={{ marginTop: 'var(--p7-space-4)' }}>
              <h5 className="p7-eyebrow" style={{ margin: '0 0 6px' }}>Evidence</h5>
              {[['Evaluations', num(trust.evaluations)], ['Blocked', num(trust.blocked)],
                ['Warned', num(trust.warned)], ['Audited', num(trust.audited)],
                ['Window', `${trust.windowDays || 90} days`]].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', padding: '6px 0',
                                        borderTop: '1px solid var(--p7-line-soft)',
                                        font: 'var(--p7-text-ui)',
                                        fontFamily: 'var(--p7-font-sans)' }}>
                    {k}
                    <b style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{v}</b>
                  </div>
                ))}
            </div>

            <p style={{ marginTop: 'var(--p7-space-4)', paddingTop: 'var(--p7-space-3)',
                        borderTop: '1px solid var(--p7-line-soft)',
                        font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink-subtle)', lineHeight: 1.5 }}>
              The score and its confidence are read together: confidence is how far this agent’s
              evidence has reached the number of evaluations your scoring policy asks for before
              acting on it.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ font: '700 20px/26px var(--p7-font-sans)',
                    fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div className="p7-eyebrow">{label}</div>
    </div>
  );
}

const VERDICT = {
  ALLOW: ['✓ Allowed', 'p7-status'],
  DENY: ['Refused', 'p7-status p7-status--deny'],
  TRANSFORM: ['Transformed', 'p7-status p7-status--running'],
};

function Audit({ rows }) {
  if (!rows.length) {
    return <div className="p7-note">Nothing on the record yet. Its first protected call will
      appear here, sealed.</div>;
  }
  return (
    <>
      <div className="p7-card" style={{ padding: 0 }}>
        {rows.map((r, i) => {
          const [label, cls] = VERDICT[r.decision] || [r.decision || '—', 'p7-status'];
          return (
            <div key={r.id || i} data-testid="audit-row"
                 style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                          padding: 'var(--p7-space-3) var(--p7-space-4)',
                          borderTop: i ? '1px solid var(--p7-line-soft)' : 'none' }}>
              <span className="p7-mono" style={{ flex: 'none', width: '52px',
                                                 color: 'var(--p7-ink-faint)' }}>
                {String(r.occurredAt || '').slice(11, 16) || '—'}
              </span>
              <span style={{ flex: 1, minWidth: 0, font: 'var(--p7-text-ui)',
                             fontFamily: 'var(--p7-font-sans)' }}>{r.action}</span>
              <span className={cls}>{label}</span>
              <span className="p7-mono" style={{ width: '90px', textAlign: 'right',
                                                 color: 'var(--p7-ink-subtle)' }}>
                {r.ruleName || '—'}
              </span>
            </div>
          );
        })}
      </div>
      <p style={{ marginTop: 'var(--p7-space-3)', font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
        Every line sealed and replayable. Nothing it does escapes this.
      </p>
    </>
  );
}

const LAYERS = [
  ['Identity', 'The caller is this agent, not the person holding its key.'],
  ['Intent', 'The request is inside what the charter says it is for.'],
  ['Access', 'The grant covers this resource, scoped to this agent alone.'],
  ['Data', 'Anything the rules hold back is removed before the answer leaves the boundary.'],
  ['Execution', 'The command runs exactly as checked. Nothing rewrites it on the way out.'],
];

function Verdict() {
  return (
    <>
      <div className="p7-card" style={{ padding: 0 }}>
        {LAYERS.map(([n, d], i) => (
          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                                padding: 'var(--p7-space-3) var(--p7-space-4)',
                                borderTop: i ? '1px solid var(--p7-line-soft)' : 'none' }}>
            <span className="p7-mono" style={{ flex: 'none', width: '18px',
                                               color: 'var(--p7-ink-faint)' }}>{i + 1}</span>
            <span style={{ width: '150px', flex: 'none', font: 'var(--p7-text-ui)',
                           fontFamily: 'var(--p7-font-sans)', fontWeight: 600 }}>{n}</span>
            <span style={{ flex: 1, minWidth: 0, font: 'var(--p7-text-caption)',
                           fontFamily: 'var(--p7-font-sans)',
                           color: 'var(--p7-ink-subtle)' }}>{d}</span>
          </div>
        ))}
      </div>
      <p style={{ marginTop: 'var(--p7-space-3)', font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
        Judgement happens in the layers above. The last one may refuse — it may never rewrite.
      </p>
    </>
  );
}

function Gates({ gates, trust, ran }) {
  const cleared = useMemo(
    () => new Set(gates.filter((g) => g.status === 'passed').map((g) => g.toPhase)),
    [gates],
  );
  const rows = [
    ['Build', 'At least one skill and one eval on record.'],
    ['Decide', 'Policy bound and attested by the accountable owner.'],
    ['Act', 'Running under protection.'],
    ['Autonomous', ran
      ? `Needs a Full trust band. This agent is at ${trust.currentScore} · ${trust.band}.`
      : 'Needs a Full trust band, which is earned at run time. This agent has not run yet.'],
  ];
  return (
    <div className="p7-card" style={{ padding: 0 }}>
      {rows.map(([n, d], i) => {
        const done = cleared.has(n) || cleared.has(n.toUpperCase());
        return (
          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                                padding: 'var(--p7-space-4)',
                                borderTop: i ? '1px solid var(--p7-line-soft)' : 'none' }}>
            <span style={{ width: '92px', flex: 'none', font: 'var(--p7-text-ui)',
                           fontFamily: 'var(--p7-font-sans)', fontWeight: 600 }}>{n}</span>
            <span style={{ flex: 1, minWidth: 0, font: 'var(--p7-text-caption)',
                           fontFamily: 'var(--p7-font-sans)',
                           color: 'var(--p7-ink-subtle)' }}>{d}</span>
            <span className={done ? 'p7-status p7-status--allow'
              : n === 'Autonomous' ? 'p7-status p7-status--running' : 'p7-status'}>
              {done ? '✓ Cleared' : n === 'Autonomous' ? 'Held' : 'Not yet'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
